import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ShieldAlert, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";

const ForzarCambioPassword = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotifications();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!user) return <Navigate to="/auth" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      showError("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (newPassword !== confirmPassword) {
      showError("Las contraseñas no coinciden");
      return;
    }

    setSubmitting(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (updErr) throw updErr;

      const { error: profErr } = await supabase
        .from("profiles")
        .update({ must_change_password: false })
        .eq("id", user.id);
      if (profErr) throw profErr;

      await queryClient.invalidateQueries({ queryKey: ["profile"] });
      success("Contraseña actualizada", {
        description: "Ya podés usar la app normalmente.",
      });
      window.location.href = "/";
    } catch (err: any) {
      console.error("Force password change error:", err);
      showError(err.message || "No se pudo actualizar la contraseña");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">Crea una nueva contraseña</h1>
          <p className="text-sm text-muted-foreground">
            Por seguridad, definí tu propia contraseña antes de continuar.
          </p>
        </div>

        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Tu contraseña fue restablecida</AlertTitle>
          <AlertDescription>
            El gimnasio te entregó una contraseña temporal. Cambiala ahora para
            poder usar la app.
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-border/50 bg-card/30 p-6">
          <div className="space-y-2">
            <Label htmlFor="newPassword">Nueva contraseña</Label>
            <Input
              id="newPassword"
              type="password"
              autoFocus
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              placeholder="••••••••"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              placeholder="••••••••"
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Guardando..." : "Guardar nueva contraseña"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ForzarCambioPassword;
